'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Tap Water by US City: 2026 Guide"
      author="Elise Maren"
      date="June 1, 2026"
      readTime="13 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Microplastics in tap water by US city 2026 guide"
    >
      <BlogLastUpdated iso="2026-06-01" formatted="June 1, 2026" />

      <BlogTLDR>
        The 2017 Orb Media investigation tested tap water in major US cities
        and found microplastics in every sample drawn. Detection rates,
        particle counts, and dominant sources vary city-to-city.{' '}
        <strong>Portland OR, Seattle, San Francisco, and NYC</strong>{' '}
        consistently sit near the lower end of US tap-water microplastic
        load because their source water comes from protected upland
        reservoirs.{' '}
        <strong>Houston, Phoenix, New Orleans, and parts of Los Angeles</strong>{' '}
        sit higher, driven by Colorado River / Mississippi source water,
        atmospheric deposition, and saltwater intrusion. Across every city
        a household point-of-use filter is the dominant lever.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The 2017 Orb Media sampling found microplastics in all 33 tested US tap-water locations including Trump Tower, EPA HQ, and the US Capitol.</>,
          <>NYC, Portland OR, Seattle, and San Francisco draw from protected upland reservoirs and tend to test lowest.</>,
          <>Houston, Phoenix, New Orleans, and parts of LA draw from river systems with higher microplastic load.</>,
          <>Chicago and Milwaukee use Lake Michigan, which sits in the middle of the US range.</>,
          <>Local utility, source-water type, and home plumbing matter more than city brand, a point-of-use filter normalises the household exposure.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What we actually know about US city tap water
      </h2>
      <p>
        The 2017 Orb Media investigation, in partnership with the University
        of Minnesota School of Public Health, remains the most-cited
        per-city dataset for US tap water. Their sampling found
        microplastics in every one of the 33 US sites tested, including
        Manhattan, Washington DC (the EPA building, the US Capitol, and
        Trump Tower), Chicago, and Boston. The detection rate of 94% across
        the broader US sample was the highest of the 14 countries Orb
        tested.
      </p>
      <p>
        Since 2017, individual academic studies have sampled specific
        utilities (Chicago, Cleveland, Denver, San Francisco Bay Area) and the EPA, USGS, and individual state agencies have begun
        broader monitoring. California adopted the first statewide
        microplastic drinking-water monitoring rule in 2022 with reporting
        phased in 2025-2026. As of mid-2026 we still don&apos;t have a
        comprehensive head-to-head city ranking, but we have enough proxy
        and direct data to characterise the major metros.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        US cities with the cleanest tap water
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Portland, Oregon. Bull Run watershed
      </h3>
      <p>
        Portland draws from the Bull Run watershed, a federally protected
        forested reservoir system in the Cascades. Until recently Portland
        delivered Bull Run water without conventional filtration, the
        source was that clean. A federal LT2 rule now requires Portland
        to build filtration by 2027, but the source water itself remains
        among the cleanest tap-water inputs in the country. Bull Run
        microplastic load is below US averages and very likely below most
        international tap-water averages.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Seattle, Washington. Cedar and Tolt watersheds
      </h3>
      <p>
        Seattle Public Utilities draws from the Cedar River and South Fork
        Tolt watersheds, both protected mountain reservoirs. Treatment uses
        ozone, UV, and chlorine, among the most advanced multi-barrier
        treatment chains in the country. Independent sampling of Seattle
        tap water has shown low microplastic counts relative to US
        averages.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        New York City. Catskill / Delaware reservoirs
      </h3>
      <p>
        Despite the city&apos;s density, NYC tap water comes from one of
        the largest and cleanest unfiltered municipal supplies in the
        country. The Catskill / Delaware reservoir system spans 2,000
        square miles of protected forested land 100+ miles north of the
        city. NYC has a Filtration Avoidance Determination from the EPA, one of only a handful of major US cities allowed to operate
        unfiltered. UV disinfection was added in 2013 at the Catskill /
        Delaware UV facility. Local distribution-system contamination
        does add some microplastic load between Westchester and your tap.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        San Francisco. Hetch Hetchy
      </h3>
      <p>
        San Francisco draws from Hetch Hetchy reservoir in Yosemite
        National Park. Like NYC, SF has historically operated without
        conventional filtration of the Hetch Hetchy source because the
        Yosemite watershed is that protected. UV disinfection was added in
        2011. Hetch Hetchy water is among the cleanest tap-water sources
        in the US.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Boston. Quabbin and Wachusett reservoirs
      </h3>
      <p>
        Massachusetts Water Resources Authority serves Boston and 50+
        surrounding communities from the Quabbin and Wachusett reservoirs
        in central Massachusetts, both protected forested supplies. MWRA
        uses ozone, UV, and chlorine. Independent sampling has shown low
        microplastic indicators.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        US cities with higher microplastic load
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Houston. Trinity River system
      </h3>
      <p>
        Houston Public Works draws primarily from the Trinity River system
        (Lake Houston, Lake Conroe, Lake Livingston) plus groundwater
        wells. The Trinity drains a heavily urbanised watershed with
        agricultural and industrial input. Combined with the Gulf coast
        humidity and the concentration of petrochemical industry in the
        Houston metro, Houston shows elevated tap-water microplastic
        indicators. Houston also has aging distribution piping in many
        older neighbourhoods.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Phoenix and Tucson. Colorado River / Salt River
      </h3>
      <p>
        Phoenix draws from the Salt River Project (Salt and Verde rivers)
        and a Central Arizona Project allocation of Colorado River water.
        Tucson is heavily dependent on CAP Colorado River water. The
        Colorado River is the dominant source water for ~40 million
        people across seven Western states, and its raw-water microplastic
        load has been rising with watershed development. Phoenix
        utilities use advanced treatment (Val Vista, Squaw Peak, Verde,
        and Deer Valley plants), but the input load is higher than
        upland-reservoir cities.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        New Orleans. Mississippi River
      </h3>
      <p>
        New Orleans Sewerage and Water Board draws directly from the
        Mississippi River, the largest drainage basin in North America.
        The Mississippi carries agricultural, industrial, and urban
        plastic load from 31 states and two Canadian provinces by the
        time it reaches New Orleans. The city is also Gulf coast with
        the associated humidity-driven plastic breakdown and atmospheric
        deposition. New Orleans tap water is among the higher-load US
        municipal supplies.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Los Angeles. State Water Project + Colorado River + local
      </h3>
      <p>
        LADWP draws from a portfolio: about 50% imported State Water
        Project (Northern California), ~20% Colorado River via Metropolitan
        Water District, ~10% Eastern Sierra Mountain runoff via the LA
        Aqueduct, and the rest from local groundwater. The imported
        components mean LA inherits whatever microplastic load the source
        watersheds carry. The Eastern Sierra water is among the cleanest;
        Colorado River the highest. LADWP runs modern treatment, but
        end-of-tap exposure depends partly on which source dominates your
        neighbourhood that month.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        US cities in the middle of the range
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Chicago and Milwaukee. Lake Michigan
      </h3>
      <p>
        Chicago&apos;s Jardine and South water plants draw from Lake
        Michigan, as does Milwaukee Water Works. Lake Michigan has
        documented microplastic concentrations from urban runoff and
        atmospheric deposition, but the volume of the lake provides
        significant dilution. Both cities operate modern multi-barrier
        treatment. Net microplastic load sits mid-range for US municipal
        supplies, better than river-fed Gulf cities, higher than upland-
        reservoir cities.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Denver. South Platte and mountain reservoirs
      </h3>
      <p>
        Denver Water draws from a combination of South Platte River
        diversions and mountain reservoirs (Dillon, Williams Fork, Cheesman).
        The mountain reservoir components keep Denver in the lower-mid
        range for microplastic load. Treatment is modern.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Atlanta. Chattahoochee River
      </h3>
      <p>
        Atlanta Department of Watershed Management draws from the
        Chattahoochee River. River-sourced and with a moderately developed
        upstream watershed, Atlanta sits mid-range. Treatment is good.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Philadelphia. Delaware and Schuylkill rivers
      </h3>
      <p>
        Philadelphia Water draws from the Delaware and Schuylkill rivers.
        Both source waters carry urban and industrial load from the
        upstream metro. Philly&apos;s treatment is modern but the source
        load and very old distribution piping in the urban core combine to
        put Philadelphia in the middle-to-upper US range.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Quick-reference city comparison
      </h2>

      <BlogComparisonTable
        caption="Major US cities ranked by proxy indicators for tap-water microplastic load (directional, not measurement)"
        columns={[
          { key: 'city', label: 'City' },
          { key: 'source', label: 'Primary source' },
          { key: 'treatment', label: 'Treatment' },
          { key: 'relative', label: 'Relative load' },
        ]}
        rows={[
          { city: 'Portland, OR', source: 'Bull Run watershed', treatment: 'High (filtration coming 2027)', relative: 'Lowest tier' },
          { city: 'Seattle, WA', source: 'Cedar / Tolt', treatment: 'Ozone + UV + chlorine', relative: 'Lowest tier' },
          { city: 'San Francisco, CA', source: 'Hetch Hetchy', treatment: 'UV + chlorine (no conventional filter)', relative: 'Lowest tier' },
          { city: 'New York, NY', source: 'Catskill / Delaware', treatment: 'UV + chlorine (filtration avoidance)', relative: 'Low' },
          { city: 'Boston, MA', source: 'Quabbin / Wachusett', treatment: 'Ozone + UV + chlorine', relative: 'Low' },
          { city: 'Denver, CO', source: 'Mountain reservoirs + South Platte', treatment: 'Conventional + UV', relative: 'Low-moderate' },
          { city: 'Chicago, IL', source: 'Lake Michigan', treatment: 'Conventional + chlorine', relative: 'Moderate' },
          { city: 'Milwaukee, WI', source: 'Lake Michigan', treatment: 'Ozone + biofiltration', relative: 'Moderate' },
          { city: 'Atlanta, GA', source: 'Chattahoochee River', treatment: 'Conventional', relative: 'Moderate' },
          { city: 'Minneapolis, MN', source: 'Mississippi headwaters', treatment: 'Lime softening + conventional', relative: 'Moderate' },
          { city: 'Detroit, MI', source: 'Lake Huron / Detroit River', treatment: 'Conventional', relative: 'Moderate' },
          { city: 'Washington, DC', source: 'Potomac River', treatment: 'Conventional + chlorine', relative: 'Moderate' },
          { city: 'Philadelphia, PA', source: 'Delaware / Schuylkill rivers', treatment: 'Conventional', relative: 'Moderate-high' },
          { city: 'Los Angeles, CA', source: 'SWP + Colorado + LA Aqueduct + local', treatment: 'Advanced', relative: 'Moderate-high (varies by zone)' },
          { city: 'Phoenix, AZ', source: 'Salt / Verde + Colorado (CAP)', treatment: 'Advanced membrane', relative: 'Moderate-high' },
          { city: 'Las Vegas, NV', source: 'Lake Mead / Colorado', treatment: 'Advanced membrane', relative: 'Moderate-high' },
          { city: 'Dallas / Fort Worth, TX', source: 'Trinity River reservoirs', treatment: 'Conventional + ozone', relative: 'Moderate-high' },
          { city: 'Houston, TX', source: 'Trinity River + groundwater', treatment: 'Conventional', relative: 'High' },
          { city: 'New Orleans, LA', source: 'Mississippi River', treatment: 'Conventional', relative: 'High' },
          { city: 'Miami / Tampa, FL', source: 'Floridan aquifer + surface', treatment: 'Lime softening / RO', relative: 'High' },
        ]}
      />
      <p className="text-sm text-gray-500 italic">
        Note: relative load is a directional proxy combining source-water
        type, treatment maturity, distribution-piping age, and independent
        microplastic sampling where it exists. It is not a measurement
        and should not be used to compare utility performance directly.
        Check your local Consumer Confidence Report for utility-specific
        contaminant data.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why your specific home matters more than your city
      </h2>
      <p>
        Even within a single city, microplastic exposure at your tap
        depends on:
      </p>
      <ul className="list-disc pl-6 space-y-2 text-gray-700">
        <li>
          <strong>Service-line material</strong>, plastic (PVC, HDPE, PEX)
          service lines shed more microplastics into the home than copper
          or iron mains.
        </li>
        <li>
          <strong>Home plumbing</strong>. PEX and CPVC plastic plumbing
          inside the walls adds load. Older copper plumbing adds less
          plastic but introduces other concerns.
        </li>
        <li>
          <strong>Hot water heater</strong>, plastic-lined hot water
          heaters and plastic dip tubes are a significant source.
        </li>
        <li>
          <strong>Faucet aerator and fixtures</strong>, most modern
          aerators have plastic components. Cheap fixtures often use
          higher-plastic-content internal parts.
        </li>
        <li>
          <strong>Time-of-day flow</strong>, first-draw water that&apos;s
          been sitting in the line overnight has higher contact load than
          flushed water.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical advice for any US city
      </h2>
      <ol className="list-decimal pl-6 space-y-2 text-gray-700">
        <li>
          <strong>Find your local Consumer Confidence Report.</strong> Every
          public utility publishes one by July 1. Look up source-water
          type, treatment process, and any PFAS detections.
        </li>
        <li>
          <strong>Cross-check the EWG Tap Water Database</strong> at
          ewg.org/tapwater for utility-specific contaminant compliance.
        </li>
        <li>
          <strong>Install a point-of-use filter.</strong> The right tier
          depends on your daily volume, see our{' '}
          <Link href="/blog/best-water-filter-pitchers-ranked" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            best pitcher filters ranked
          </Link>
          ,{' '}
          <Link href="/blog/best-reverse-osmosis-systems-for-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            best RO systems
          </Link>
          , and{' '}
          <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            best water filters overall
          </Link>
          .
        </li>
        <li>
          <strong>Flush before first draw.</strong> Run the tap for 30
          seconds before filling a glass first thing in the morning,
          especially in older buildings.
        </li>
      </ol>

      <p>
        See also:{' '}
        <Link href="/blog/microplastics-in-tap-water-by-us-state" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in tap water by US state
        </Link>
        ,{' '}
        <Link href="/blog/microplastics-in-drinking-water-by-country" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in drinking water by country
        </Link>
        , and{' '}
        <Link href="/blog/microplastics-in-tap-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in tap water (overview)
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Bottled water and filter cartridge brand and material from the barcode.</>,
          <>Container condition signals, dents, scratches, label wear.</>,
          <>Use-context flags you log, heat exposure, reuse, storage time.</>,
          <>The linked published research behind every 0-100 risk score.</>,
          <>Safer alternatives in the same product category and price band.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan bottled drinks and filter cartridges in your city"
        sub="Per-city tap-water data is patchy, but every bottle, jug, and pitcher you buy is concrete. Tap the barcode and the MicroPlastics app surfaces the material, the brand history, and a 0-100 risk score with the safer same-category alternative."
        buttonLabel="Scan water products in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Which US city has the cleanest tap water?',
            answer:
              'Portland OR (Bull Run watershed), Seattle (Cedar / Tolt), San Francisco (Hetch Hetchy), New York City (Catskill / Delaware), and Boston (Quabbin / Wachusett) draw from federally protected upland reservoirs and consistently rank among the cleanest US municipal water supplies.',
          },
          {
            question: 'Does Los Angeles tap water have microplastics?',
            answer:
              'Yes: like every US tap-water supply, LA tap contains microplastics. LADWP draws from a mix of imported State Water Project water, Colorado River, Eastern Sierra Aqueduct, and local groundwater. The imported components carry the source-watershed microplastic load. LADWP treatment is advanced; exposure at your tap depends partly on which source dominates your zone.',
          },
          {
            question: 'How does NYC tap water rank for microplastics?',
            answer:
              'NYC tap water is among the cleaner major-city supplies in the US. The Catskill / Delaware reservoir system is one of the only major-city water supplies in the country allowed to operate unfiltered because the source water is exceptionally clean. UV disinfection was added in 2013. The main downstream concern is the aging distribution piping in some neighbourhoods.',
          },
          {
            question: 'Is Chicago tap water safe to drink?',
            answer:
              'Chicago tap water meets all current federal standards. The source is Lake Michigan, which has documented microplastic concentrations but also significant dilution. Treatment is conventional plus chlorine. Microplastic load is mid-range for US municipal supplies. A point-of-use filter certified for microplastics (a Brita Elite or PUR PLUS pitcher, or RO) is recommended for households actively reducing microplastic exposure.',
          },
          {
            question: 'Why does Phoenix tap water taste different?',
            answer:
              'Phoenix-area tap water draws from a mix of Colorado River (via CAP) and Salt / Verde river reservoirs. Colorado River water has high dissolved minerals and a distinct taste profile. The treatment is advanced (most Phoenix-area utilities operate ultrafiltration plants) but the source-water signature comes through. For taste, an inline carbon filter or pitcher is the simplest fix.',
          },
          {
            question: 'Is well water in suburban areas of these cities cleaner?',
            answer:
              'Sometimes. Deep private wells drawing from protected aquifers are often among the cleanest sources, comparable to the best municipal groundwater systems. Shallow wells, wells near agricultural land, and wells using plastic casings or plastic plumbing are more exposed. Well users should test for PFAS and use a point-of-use filter for microplastics.',
          },
          {
            question: 'How do I find out what is in my specific city water?',
            answer:
              'Three places: your utility&apos;s annual Consumer Confidence Report (every public utility publishes one by July 1); the EWG Tap Water Database at ewg.org/tapwater, searchable by ZIP code; and your state environmental-quality department for any microplastic monitoring rule (California is the first state to require this, with reporting phased in 2025-2026).',
          },
          {
            question: 'What is the single best filter for any US city tap water?',
            answer:
              'For most households the highest-impact upgrade is reverse osmosis. AquaTru Classic / Carafe for renters, Waterdrop G3P800 or Frizzlife PD600 for under-sink, APEC ROES-PH75 for budget. RO removes greater than 99% of microplastics, nanoplastics, and PFAS regardless of which city you live in. For lower-cost or rental setups, Clearly Filtered Pitcher or ZeroWater are the best pitcher options.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Tyree C, Morrison D.',
            year: 2017,
            title: 'Invisibles: The Plastic Inside Us: Orb Media',
            publisher: 'Orb Media (with University of Minnesota School of Public Health)',
            url: 'https://orbmedia.org/stories/invisibles_final_report',
          },
          {
            authors: 'Smalling KL, Romanok KM, Bradley PM, et al.',
            year: 2024,
            title: 'Per- and polyfluoroalkyl substances (PFAS) in United States tapwater',
            publisher: 'USGS / Environment International',
            url: 'https://www.usgs.gov/news/national-news-release/least-45-nations-tap-water-contaminated-forever-chemicals',
          },
          {
            authors: 'California State Water Resources Control Board',
            year: 2022,
            title: 'Standard methods for microplastics in drinking water',
            publisher: 'CA SWRCB',
            url: 'https://www.waterboards.ca.gov/drinking_water/certlic/drinkingwater/microplastics.html',
          },
          {
            authors: 'US Environmental Protection Agency',
            year: 2024,
            title: 'Drinking Water Consumer Confidence Reports',
            publisher: 'EPA',
            url: 'https://www.epa.gov/ccr',
          },
          {
            authors: 'Environmental Working Group',
            year: 2024,
            title: 'EWG Tap Water Database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/tapwater/',
          },
          {
            authors: 'New York City Department of Environmental Protection',
            year: 2024,
            title: 'Catskill / Delaware Filtration Avoidance Determination',
            publisher: 'NYC DEP / EPA',
            url: 'https://www.nyc.gov/site/dep/water/filtration-avoidance.page',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
